import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    CLERK_SECRET_KEY: z.string(),
    TURNSTILE_SECRET_KEY: z.string(),
    // Go-live #22: "1" = the Convex lead action verifies the Turnstile token
    // (token is forwarded, this route stops verifying). Unset = verified here.
    LEAD_TURNSTILE_IN_CONVEX: z.string().optional(),
    NODE_ENV: z.enum(["development", "test", "production"]),
    // BLOG: shared secret with the FastAPI backend so it can call
    // /api/blog/revalidate after a publish/unpublish action. Empty
    // = revalidation requests are rejected (the route still works
    // for testing via the build-time ISR background refresh).
    BLOG_REVALIDATE_SECRET: z.string().optional(),
    // SOLUTIONS: shared secret with the FastAPI backend so it can call
    // /api/solutions/revalidate after a publish/edit. Same pattern as
    // BLOG_REVALIDATE_SECRET. Empty = revalidation requests are
    // rejected (pages still refresh on the next ISR background pass).
    SOLUTIONS_REVALIDATE_SECRET: z.string().optional(),
    // CSV of Clerk org slugs that grant admin access to /admin/*.
    // Mirrors the backend `settings.ADMIN_ORG_SLUGS`. Empty = admin
    // gate denies. Server-only: only the admin server-component
    // layouts read it, so it must NEVER be exposed as NEXT_PUBLIC_*
    // (that would inline the allowlist into the public JS bundle).
    // Optional so the variable can be absent in local dev.
    ADMIN_ORG_SLUGS: z.string().optional(),
    // MARKETS: server-only market-data provider keys, read solely inside
    // the /api/market/* route handlers (never NEXT_PUBLIC_*). Crypto
    // works with no key (CoinGecko/Coinbase public); stocks need one of
    // these — absent = the Markets tab shows "stocks not configured" and
    // crypto still works.
    FINNHUB_API_KEY: z.string().optional(),
    // Alias: the key was added to Vercel as FINNHUB_KEY; accept either
    // name so whichever one is set resolves stock data.
    FINNHUB_KEY: z.string().optional(),
    ALPHA_VANTAGE_API_KEY: z.string().optional(),
    // Sentry (OBS, #1006). Server-side DSN, read by sentry.server.config.ts
    // and sentry.edge.config.ts (loaded via instrumentation.ts). Absent
    // DSN disables Sentry — safe for local dev / unconfigured envs.
    SENTRY_DSN: z.url().optional(),
    // Sentry source-map upload at build time (next.config.js's
    // sentryWebpackPluginOptions). All three optional together — absent
    // SENTRY_AUTH_TOKEN makes the upload step a silent no-op.
    SENTRY_ORG: z.string().optional(),
    SENTRY_PROJECT: z.string().optional(),
    SENTRY_AUTH_TOKEN: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: z.string(),
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: z.string(),
    NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL: z.string(),
    NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL: z.string(),
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string(),
    NEXT_PUBLIC_API_URL: z.url(),
    NEXT_PUBLIC_CONVEX_URL: z.url(),
    // Sentry (OBS, #1006). Client-side DSN, read by
    // instrumentation-client.ts. Absent DSN disables Sentry.
    NEXT_PUBLIC_SENTRY_DSN: z.url().optional(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    CLERK_SECRET_KEY: process.env.CLERK_SECRET_KEY,
    TURNSTILE_SECRET_KEY: process.env.TURNSTILE_SECRET_KEY,
    LEAD_TURNSTILE_IN_CONVEX: process.env.LEAD_TURNSTILE_IN_CONVEX,
    BLOG_REVALIDATE_SECRET: process.env.BLOG_REVALIDATE_SECRET,
    SOLUTIONS_REVALIDATE_SECRET: process.env.SOLUTIONS_REVALIDATE_SECRET,
    ADMIN_ORG_SLUGS: process.env.ADMIN_ORG_SLUGS,
    FINNHUB_API_KEY: process.env.FINNHUB_API_KEY,
    FINNHUB_KEY: process.env.FINNHUB_KEY,
    ALPHA_VANTAGE_API_KEY: process.env.ALPHA_VANTAGE_API_KEY,
    SENTRY_DSN: process.env.SENTRY_DSN,
    SENTRY_ORG: process.env.SENTRY_ORG,
    SENTRY_PROJECT: process.env.SENTRY_PROJECT,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL:
      process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL,
    NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL:
      process.env.NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_CONVEX_URL: process.env.NEXT_PUBLIC_CONVEX_URL,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
